import { useCallback, useEffect, useState } from 'react';
import {
  Loader2,
  Pencil,
  Plus,
  Check,
  X,
  Trash2,
  RefreshCw,
  FileText,
  Lock,
  ChevronDown,
  Paperclip,
} from 'lucide-react';
import { api } from '../api/client';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

// ── Types ─────────────────────────────────────────────────────────────────────

type ConfigRow = {
  key: string;
  value: string;
  updated_at: string;
  updated_by: string | null;
};

type Period = {
  id: string;
  label: string;
  start_date: string;
  end_date: string;
  issue_date: string;
  number_prefix: string;
  next_number: number;
  closed_at: string | null;
  created_at: string;
};

type TrackingMapping = {
  category: string;
  reason: string;
  updated_at: string;
};

type DonorAggregate = {
  contactId: string;
  displayName: string;
  email: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    postcode: string | null;
  };
  currency: string;
  totalAmount: number;
  txnCount: number;
  primaryTracking: string | null;
  warnings: unknown[];
  pulledAt: string;
};

type ReceiptRow = {
  id: string;
  receipt_number: string;
  contact_id: string;
  display_name: string;
  email: string | null;
  total_amount: number;
  currency: string;
  txn_count: number;
  status: string;
  supersedes: string | null;
  tracking_label: string | null;
  email_sent_at: string | null;
  xero_attachment_id: string | null;
  created_at: string;
  error_detail: string | null;
};

type IssueOutcome = {
  receiptNumber: string;
  draftId: string;
  to: string;
};

// ── Constants ─────────────────────────────────────────────────────────────────

const ORG_CONFIG: Record<string, string> = {
  org_name: 'Organisation name',
  ird_number: 'IRD number',
  charities_number: 'Charities registration',
  authorised_name: 'Authorised signatory',
  authorised_title: 'Signatory title',
  footer_line: 'Receipt footer',
};
const ORG_CONFIG_ORDER = Object.keys(ORG_CONFIG);

const EMAIL_CONFIG: Record<
  string,
  { label: string; multiline: boolean; placeholder?: string; hideValue?: boolean }
> = {
  email_subject_template: {
    label: 'Subject',
    multiline: false,
    placeholder: 'e.g. Donation Tax Credit Receipt',
  },
  email_body_template: {
    label: 'Body',
    multiline: true,
    placeholder: 'Dear {firstName},\n\nThank you for…\n\nWith every blessing,',
  },
  email_signature: {
    label: 'Signature (HTML)',
    multiline: true,
    hideValue: true,
    placeholder:
      'HTML appended after the body. Logo example:\n<img src="https://confessinganglicans.nz/logo.png" width="120"><br>\nSteve Jukes | Registrar',
  },
  email_cc: { label: 'CC address', multiline: false, placeholder: 'e.g. admin@example.org' },
};
const EMAIL_CONFIG_ORDER = Object.keys(EMAIL_CONFIG);

const BLANK_PERIOD = {
  id: '',
  label: '',
  start_date: '',
  end_date: '',
  issue_date: '',
  number_prefix: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function derivePrefix(id: string): string {
  const m = id.match(/[a-z]+(\d{2})(\d{2})$/i);
  if (m) return `20${m[1]}${m[2]}`;
  return '';
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'drafted'
      ? 'pill pill-warn'
      : status === 'sent' || status === 'done' || status === 'emailed'
        ? 'pill pill-success'
        : status === 'superseded'
          ? 'text-[11px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-400'
          : // 'failed' = an orphaned/reclaimed re-issue tombstone (cca-zah8 R2):
            // kept visible in red for reconciliation, never hidden.
            status === 'error' || status === 'failed'
            ? 'text-[11px] font-medium px-2 py-0.5 rounded bg-red-100 text-red-700'
            : status === 'reserving'
              ? 'text-[11px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-500'
              : 'pill';
  const label = status === 'failed' ? 'failed (orphan)' : status;
  return <span className={cls}>{label}</span>;
}

// Section header: title + chevron (clickable to collapse), optional action button
function SectionHeader({
  title,
  sectionId,
  collapsed,
  onToggle,
  action,
}: {
  title: string;
  sectionId: string;
  collapsed: boolean;
  onToggle: (id: string) => void;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-1">
      <button
        type="button"
        onClick={() => onToggle(sectionId)}
        className="flex items-center gap-1.5 text-left group"
      >
        <h2 className="panel-section-title group-hover:text-ccaanz-black">{title}</h2>
        <ChevronDown
          className={`w-4 h-4 text-ccaanz-grey flex-shrink-0 transition-transform duration-150 ${
            collapsed ? '' : 'rotate-180'
          }`}
        />
      </button>
      {!collapsed && action}
    </div>
  );
}

// ── Page component ─────────────────────────────────────────────────────────────

export function ReceiptsPage() {
  // ── Admin data ────────────────────────────────────────────────────────────
  const [config, setConfig] = useState<ConfigRow[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [mappings, setMappings] = useState<TrackingMapping[]>([]);
  const [trackingOptions, setTrackingOptions] = useState<string[]>([]);
  const [xeroConnected, setXeroConnected] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Section collapse state ────────────────────────────────────────────────
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('receipts_collapsed');
      return stored ? (JSON.parse(stored) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  const toggleSection = (id: string) =>
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem('receipts_collapsed', JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  // ── Config inline edit ────────────────────────────────────────────────────
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  // ── Next-number inline edit ───────────────────────────────────────────────
  const [editingNextNum, setEditingNextNum] = useState<string | null>(null);
  const [editNextVal, setEditNextVal] = useState('');
  const [savingNextNum, setSavingNextNum] = useState<string | null>(null);
  const [nextNumError, setNextNumError] = useState<string | null>(null);

  // ── New period form ───────────────────────────────────────────────────────
  const [showNewPeriod, setShowNewPeriod] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ ...BLANK_PERIOD });
  const [creatingPeriod, setCreatingPeriod] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);

  // ── Close period ──────────────────────────────────────────────────────────
  const [closingPeriod, setClosingPeriod] = useState<string | null>(null);

  // ── Tracking mappings edit ────────────────────────────────────────────────
  const [editingMapping, setEditingMapping] = useState<string | null>(null);
  const [editMappingReason, setEditMappingReason] = useState('');
  const [savingMapping, setSavingMapping] = useState<string | null>(null);
  const [deletingMapping, setDeletingMapping] = useState<string | null>(null);
  const [showNewMapping, setShowNewMapping] = useState(false);
  const [newMappingCategory, setNewMappingCategory] = useState('');
  const [newMappingReason, setNewMappingReason] = useState('');
  const [creatingMapping, setCreatingMapping] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);

  // ── Issue workflow ────────────────────────────────────────────────────────
  const [workPeriodId, setWorkPeriodId] = useState('');
  const [pulling, setPulling] = useState(false);
  const [pullError, setPullError] = useState<string | null>(null);
  const [pullSummary, setPullSummary] = useState<{
    aggregateCount: number;
    uniqueDonorCount: number;
    pulledAt: string;
  } | null>(null);
  const [donors, setDonors] = useState<DonorAggregate[]>([]);
  const [donorsLoading, setDonorsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [issuingContact, setIssuingContact] = useState<string | null>(null);
  const [issueOutcomes, setIssueOutcomes] = useState<Record<string, IssueOutcome>>({});
  const [issueErrors, setIssueErrors] = useState<Record<string, string>>({});
  const [reissuingId, setReissuingId] = useState<string | null>(null);
  const [reissueErrors, setReissueErrors] = useState<Record<string, string>>({});
  const [attachingId, setAttachingId] = useState<string | null>(null);
  const [attachErrors, setAttachErrors] = useState<Record<string, string>>({});
  const [editingDonorKey, setEditingDonorKey] = useState<string | null>(null);
  const [editDonorValues, setEditDonorValues] = useState({
    email: '',
    addr_line1: '',
    addr_line2: '',
    city: '',
    postcode: '',
  });
  const [savingDonorKey, setSavingDonorKey] = useState<string | null>(null);
  const [donorEditError, setDonorEditError] = useState<string | null>(null);
  const [issuingAll, setIssuingAll] = useState(false);
  const [issueAllProgress, setIssueAllProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [receiptsForPeriod, setReceiptsForPeriod] = useState<ReceiptRow[]>([]);

  // ── Load admin data ───────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, prd, trk, xeroTrk] = await Promise.all([
        api.receiptsConfig(),
        api.receiptsPeriods(),
        api.receiptsTrackingMappings(),
        api.receiptsXeroTrackingOptions(),
      ]);
      setConfig(cfg.config);
      setPeriods(prd.periods);
      setMappings(trk.mappings);
      setTrackingOptions(xeroTrk.options);
      setXeroConnected(xeroTrk.xeroConnected);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Config handlers (shared for org + email sections) ────────────────────
  const startEdit = (key: string) => {
    const row = config.find((r) => r.key === key);
    setEditValue(row?.value ?? '');
    setEditingKey(key);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveEdit = async (key: string) => {
    setSavingKey(key);
    try {
      const updated = await api.receiptsConfigSet(key, editValue.trim());
      setConfig((prev) => {
        const exists = prev.some((r) => r.key === key);
        const row: ConfigRow = {
          key,
          value: updated.value,
          updated_at: new Date().toISOString(),
          updated_by: null,
        };
        return exists ? prev.map((r) => (r.key === key ? row : r)) : [...prev, row];
      });
      setEditingKey(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingKey(null);
    }
  };

  // ── Next-number handlers ──────────────────────────────────────────────────
  const startEditNextNum = (p: Period) => {
    setEditNextVal(String(p.next_number));
    setNextNumError(null);
    setEditingNextNum(p.id);
  };

  const cancelEditNextNum = () => {
    setEditingNextNum(null);
    setNextNumError(null);
  };

  const saveNextNum = async (p: Period) => {
    const val = parseInt(editNextVal, 10);
    if (isNaN(val) || val < 1) {
      setNextNumError('Must be a positive whole number.');
      return;
    }
    if (val <= p.next_number) {
      setNextNumError(`Must be greater than current value (${p.next_number}).`);
      return;
    }
    setSavingNextNum(p.id);
    setNextNumError(null);
    try {
      const res = await api.receiptsSetNextNumber(p.id, val);
      setPeriods((prev) =>
        prev.map((period) =>
          period.id === p.id ? { ...period, next_number: res.next_number } : period,
        ),
      );
      setEditingNextNum(null);
    } catch (e) {
      const msg = (e as Error).message;
      setNextNumError(
        msg.includes('422') ? `Value not higher than current (${p.next_number}).` : msg,
      );
    } finally {
      setSavingNextNum(null);
    }
  };

  // ── Mapping handlers ──────────────────────────────────────────────────────
  const startEditMapping = (m: TrackingMapping) => {
    setEditMappingReason(m.reason);
    setEditingMapping(m.category);
  };

  const cancelEditMapping = () => {
    setEditingMapping(null);
    setEditMappingReason('');
  };

  const saveEditMapping = async (category: string) => {
    setSavingMapping(category);
    try {
      const updated = await api.receiptsTrackingMappingSet(category, editMappingReason.trim());
      setMappings((prev) =>
        prev.map((m) =>
          m.category === category
            ? { ...m, reason: updated.reason, updated_at: new Date().toISOString() }
            : m,
        ),
      );
      setEditingMapping(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingMapping(null);
    }
  };

  const deleteMapping = async (category: string) => {
    if (!window.confirm(`Remove the mapping for "${category}"?`)) return;
    setDeletingMapping(category);
    try {
      await api.receiptsTrackingMappingDelete(category);
      setMappings((prev) => prev.filter((m) => m.category !== category));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeletingMapping(null);
    }
  };

  const handleCreateMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    setMappingError(null);
    const cat = newMappingCategory.trim();
    const reason = newMappingReason.trim();
    if (!cat || !reason) {
      setMappingError('Both category and reason are required.');
      return;
    }
    setCreatingMapping(true);
    try {
      const created = await api.receiptsTrackingMappingSet(cat, reason);
      setMappings((prev) => {
        const exists = prev.some((m) => m.category === cat);
        const row: TrackingMapping = {
          category: cat,
          reason: created.reason,
          updated_at: new Date().toISOString(),
        };
        return exists ? prev.map((m) => (m.category === cat ? row : m)) : [...prev, row];
      });
      setNewMappingCategory('');
      setNewMappingReason('');
      setShowNewMapping(false);
    } catch (e) {
      setMappingError((e as Error).message);
    } finally {
      setCreatingMapping(false);
    }
  };

  // ── Period form handlers ──────────────────────────────────────────────────
  const handlePeriodIdChange = (id: string) => {
    setNewPeriod((p) => ({
      ...p,
      id,
      number_prefix:
        p.number_prefix === '' || p.number_prefix === derivePrefix(p.id)
          ? derivePrefix(id)
          : p.number_prefix,
    }));
  };

  const handleCreatePeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    setPeriodError(null);
    setCreatingPeriod(true);
    try {
      await api.receiptsCreatePeriod({
        id: newPeriod.id.trim(),
        label: newPeriod.label.trim(),
        start_date: newPeriod.start_date,
        end_date: newPeriod.end_date,
        issue_date: newPeriod.issue_date,
        number_prefix: newPeriod.number_prefix.trim() || derivePrefix(newPeriod.id),
      });
      setNewPeriod({ ...BLANK_PERIOD });
      setShowNewPeriod(false);
      await refresh();
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes('409')) {
        setPeriodError(`Period ID "${newPeriod.id}" already exists.`);
      } else {
        setPeriodError(msg);
      }
    } finally {
      setCreatingPeriod(false);
    }
  };

  const handleClosePeriod = async (id: string) => {
    const p = periods.find((x) => x.id === id);
    if (
      !window.confirm(
        `Close period "${p?.label ?? id}"?\n\nThis will prevent any further receipts being issued for this period. You cannot reopen a closed period.`,
      )
    )
      return;
    setClosingPeriod(id);
    try {
      await api.receiptsClosePeriod(id);
      setPeriods((prev) =>
        prev.map((x) => (x.id === id ? { ...x, closed_at: new Date().toISOString() } : x)),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setClosingPeriod(null);
    }
  };

  // ── Issue workflow handlers ───────────────────────────────────────────────
  const loadDonorsAndReceipts = useCallback(async (pid: string) => {
    setDonorsLoading(true);
    setPullError(null);
    try {
      const [donorRes, receiptRes] = await Promise.all([
        api.receiptsDonors(pid),
        api.receiptsListReceipts(pid),
      ]);
      setDonors(donorRes.aggregates);
      setPullSummary(
        donorRes.aggregates.length > 0
          ? {
              aggregateCount: donorRes.aggregateCount,
              uniqueDonorCount: donorRes.uniqueDonorCount,
              pulledAt: donorRes.aggregates[0]?.pulledAt ?? '',
            }
          : null,
      );
      setReceiptsForPeriod(receiptRes.receipts);
    } catch (e) {
      setPullError((e as Error).message);
    } finally {
      setDonorsLoading(false);
    }
  }, []);

  const handleWorkPeriodChange = async (pid: string) => {
    setWorkPeriodId(pid);
    setDonors([]);
    setPullSummary(null);
    setPullError(null);
    setIssueOutcomes({});
    setIssueErrors({});
    setReceiptsForPeriod([]);
    if (!pid) return;
    await loadDonorsAndReceipts(pid);
  };

  const handlePull = async () => {
    if (!workPeriodId) return;
    setPulling(true);
    setPullError(null);
    try {
      const res = await api.receiptsPull(workPeriodId);
      setPullSummary({
        aggregateCount: res.aggregateCount,
        uniqueDonorCount: res.uniqueDonorCount,
        pulledAt: res.pulledAt,
      });
      await loadDonorsAndReceipts(workPeriodId);
    } catch (e) {
      setPullError((e as Error).message);
    } finally {
      setPulling(false);
    }
  };

  const handlePreview = async (contactId: string, currency: string) => {
    const key = `${contactId}:${currency}`;
    setPreviewLoading(key);
    try {
      const token = localStorage.getItem('xero_session_token');
      const url = `${API_BASE}/api/receipts/preview?periodId=${encodeURIComponent(workPeriodId)}&contactId=${encodeURIComponent(contactId)}&currency=${encodeURIComponent(currency)}`;
      const res = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          ...(token ? { 'X-Session-Token': token } : {}),
        },
      });
      if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleIssue = async (contactId: string, currency: string) => {
    const key = `${contactId}:${currency}`;
    setIssuingContact(key);
    setIssueErrors((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
    try {
      const res = await api.receiptsIssueAndDraft(workPeriodId, contactId, currency);
      setIssueOutcomes((prev) => ({
        ...prev,
        [key]: { receiptNumber: res.receiptNumber, draftId: res.draftId, to: res.to },
      }));
      const receiptRes = await api.receiptsListReceipts(workPeriodId);
      setReceiptsForPeriod(receiptRes.receipts);
    } catch (e) {
      const msg = (e as Error).message;
      let friendly = msg;
      if (msg.includes('422')) friendly = 'Email address missing for this donor.';
      else if (msg.includes('409')) friendly = 'Period is closed or conflict — retry.';
      else if (msg.includes('500')) friendly = 'Issue failed — check worker logs.';
      setIssueErrors((prev) => ({ ...prev, [key]: friendly }));
    } finally {
      setIssuingContact(null);
    }
  };

  const handleReissue = async (receiptId: string) => {
    setReissuingId(receiptId);
    setReissueErrors((prev) => {
      const { [receiptId]: _removed, ...rest } = prev;
      return rest;
    });
    try {
      await api.receiptsReissue(receiptId);
      if (workPeriodId) {
        const receiptRes = await api.receiptsListReceipts(workPeriodId);
        setReceiptsForPeriod(receiptRes.receipts);
      }
    } catch (e) {
      const msg = (e as Error).message;
      let friendly = msg;
      if (msg.includes('409'))
        friendly = 'Re-issue blocked — already superseded, or a re-issue is already in progress.';
      else if (msg.includes('422')) friendly = 'No donations found in Xero, or email missing.';
      else if (msg.includes('404')) friendly = 'Receipt not found.';
      else if (msg.includes('500')) friendly = 'Re-issue failed — check worker logs.';
      setReissueErrors((prev) => ({ ...prev, [receiptId]: friendly }));
    } finally {
      setReissuingId(null);
    }
  };

  const handleXeroAttach = async (receiptId: string) => {
    setAttachingId(receiptId);
    setAttachErrors((prev) => {
      const { [receiptId]: _removed, ...rest } = prev;
      return rest;
    });
    try {
      await api.receiptsXeroAttach(receiptId);
      if (workPeriodId) {
        const receiptRes = await api.receiptsListReceipts(workPeriodId);
        setReceiptsForPeriod(receiptRes.receipts);
      }
    } catch (e) {
      const msg = (e as Error).message;
      let friendly = msg;
      if (msg.includes('409')) friendly = 'Already attached to Xero.';
      else if (msg.includes('422')) friendly = 'No PDF stored for this receipt.';
      else if (msg.includes('500')) friendly = 'Xero attachment failed — check worker logs.';
      setAttachErrors((prev) => ({ ...prev, [receiptId]: friendly }));
    } finally {
      setAttachingId(null);
    }
  };

  const startEditDonor = (d: DonorAggregate) => {
    setEditingDonorKey(`${d.contactId}:${d.currency}`);
    setEditDonorValues({
      email: d.email ?? '',
      addr_line1: d.address.line1 ?? '',
      addr_line2: d.address.line2 ?? '',
      city: d.address.city ?? '',
      postcode: d.address.postcode ?? '',
    });
    setDonorEditError(null);
  };

  const cancelEditDonor = () => {
    setEditingDonorKey(null);
    setDonorEditError(null);
  };

  const saveEditDonor = async (d: DonorAggregate) => {
    const key = `${d.contactId}:${d.currency}`;
    setSavingDonorKey(key);
    setDonorEditError(null);
    const patch: {
      email?: string | null;
      addr_line1?: string | null;
      addr_line2?: string | null;
      city?: string | null;
      postcode?: string | null;
    } = {};
    if (editDonorValues.email !== (d.email ?? '')) patch.email = editDonorValues.email || null;
    if (editDonorValues.addr_line1 !== (d.address.line1 ?? ''))
      patch.addr_line1 = editDonorValues.addr_line1 || null;
    if (editDonorValues.addr_line2 !== (d.address.line2 ?? ''))
      patch.addr_line2 = editDonorValues.addr_line2 || null;
    if (editDonorValues.city !== (d.address.city ?? '')) patch.city = editDonorValues.city || null;
    if (editDonorValues.postcode !== (d.address.postcode ?? ''))
      patch.postcode = editDonorValues.postcode || null;
    if (Object.keys(patch).length === 0) {
      setEditingDonorKey(null);
      setSavingDonorKey(null);
      return;
    }
    try {
      const res = await api.receiptsPatchDonor(d.contactId, patch);
      setDonors((prev) =>
        prev.map((donor) =>
          donor.contactId === d.contactId && donor.currency === d.currency
            ? {
                ...donor,
                email: res.donor.email,
                address: {
                  line1: res.donor.addr_line1,
                  line2: res.donor.addr_line2,
                  city: res.donor.city,
                  postcode: res.donor.postcode,
                },
              }
            : donor,
        ),
      );
      setEditingDonorKey(null);
    } catch (e) {
      const msg = (e as Error).message;
      let friendly = msg;
      if (msg.includes('email_invalid')) friendly = 'Invalid email address.';
      else if (msg.includes('502')) friendly = 'Xero update failed — check Xero connection.';
      else if (msg.includes('404')) friendly = 'Donor not found.';
      setDonorEditError(friendly);
    } finally {
      setSavingDonorKey(null);
    }
  };

  const handleIssueAll = async () => {
    if (!workPeriodId || issuingAll) return;
    const unissued = donors.filter((d) => {
      const key = `${d.contactId}:${d.currency}`;
      const existingReceipt = receiptsForPeriod.find(
        (r) =>
          r.contact_id === d.contactId && r.currency === d.currency && r.status !== 'superseded',
      );
      return !existingReceipt && !issueOutcomes[key] && d.email;
    });
    if (unissued.length === 0) return;
    setIssuingAll(true);
    setIssueAllProgress({ done: 0, total: unissued.length });
    for (const [i, d] of unissued.entries()) {
      const key = `${d.contactId}:${d.currency}`;
      try {
        const res = await api.receiptsIssueAndDraft(workPeriodId, d.contactId, d.currency);
        setIssueOutcomes((prev) => ({
          ...prev,
          [key]: { receiptNumber: res.receiptNumber, draftId: res.draftId, to: res.to },
        }));
      } catch (e) {
        const msg = (e as Error).message;
        let friendly = msg;
        if (msg.includes('422')) friendly = 'Email address missing.';
        else if (msg.includes('409')) friendly = 'Period closed or conflict.';
        else if (msg.includes('500')) friendly = 'Issue failed — check worker logs.';
        setIssueErrors((prev) => ({ ...prev, [key]: friendly }));
      }
      setIssueAllProgress({ done: i + 1, total: unissued.length });
    }
    try {
      const receiptRes = await api.receiptsListReceipts(workPeriodId);
      setReceiptsForPeriod(receiptRes.receipts);
    } catch {
      /* ignore refresh failure */
    }
    setIssuingAll(false);
    setIssueAllProgress(null);
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const workPeriod = periods.find((p) => p.id === workPeriodId);

  // ── Config field renderer (shared for org + email sections) ──────────────
  const renderConfigField = (
    key: string,
    label: string,
    multiline: boolean,
    placeholder?: string,
    hideValue?: boolean,
  ) => {
    const row = config.find((r) => r.key === key);
    const isEditing = editingKey === key;
    const isSaving = savingKey === key;
    const display = row?.value ?? '';

    return (
      <div key={key} className={`py-3 ${multiline ? '' : 'flex items-center gap-3'}`}>
        {multiline ? (
          <>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[13px] text-ccaanz-grey">{label}</span>
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => startEdit(key)}
                  className="text-ccaanz-grey hover:text-ccaanz-black p-1 rounded"
                  title={`Edit ${label}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  className="input resize-y min-h-[120px] font-mono text-[12px] leading-relaxed"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={placeholder}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after an explicit Edit action
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void saveEdit(key)}
                    disabled={isSaving}
                    className="btn btn-blue btn-sm"
                  >
                    {isSaving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={isSaving}
                    className="btn btn-ghost btn-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : hideValue ? (
              <div className="py-1">
                {display ? (
                  <span className="pill pill-success">Configured</span>
                ) : (
                  <span className="text-[12px] text-ccaanz-grey italic">— not set —</span>
                )}
              </div>
            ) : (
              <div
                className={`text-[12px] whitespace-pre-wrap font-mono leading-relaxed rounded px-3 py-2 bg-paper-bg border border-paper-divider min-h-[40px] ${display ? 'text-ccaanz-black' : 'text-ccaanz-grey italic'}`}
              >
                {display || placeholder || '— not set —'}
              </div>
            )}
          </>
        ) : (
          <>
            <span className="w-40 flex-shrink-0 text-[13px] text-ccaanz-grey">{label}</span>
            {isEditing ? (
              <div className="flex-1 flex items-center gap-2">
                <input
                  className="input flex-1"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={placeholder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void saveEdit(key);
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after an explicit Edit action
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => void saveEdit(key)}
                  disabled={isSaving}
                  className="btn btn-blue btn-sm"
                >
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={isSaving}
                  className="btn btn-ghost btn-sm"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                <span
                  className={`text-[13px] truncate ${display ? 'text-ccaanz-black' : 'text-ccaanz-grey italic'}`}
                >
                  {display || '— not set —'}
                </span>
                <button
                  type="button"
                  onClick={() => startEdit(key)}
                  className="flex-shrink-0 text-ccaanz-grey hover:text-ccaanz-black p-1 rounded"
                  title={`Edit ${label}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[640px] mx-auto py-6 space-y-6">
      {error && (
        <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded px-4 py-3">
          {error}
        </div>
      )}

      {/* ── Issue receipts ──────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-section">
          <SectionHeader
            title="Issue receipts"
            sectionId="issue"
            collapsed={!!collapsed.issue}
            onToggle={toggleSection}
          />
          {!collapsed.issue && (
            <>
              <p className="panel-section-sub mb-3">
                Select a period, pull eligible donors from Xero, then issue receipts one by one.
                Each issued receipt creates a Gmail draft for you to review and send.
              </p>

              {/* Period selector + pull button */}
              <div className="flex items-center gap-3 flex-wrap">
                {loading ? (
                  <div className="flex items-center gap-2 text-[13px] text-ccaanz-grey">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : (
                  <>
                    <select
                      className="input flex-1 max-w-[280px]"
                      value={workPeriodId}
                      onChange={(e) => void handleWorkPeriodChange(e.target.value)}
                    >
                      <option value="">— select a period —</option>
                      {periods.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                          {p.closed_at ? ' (closed)' : ''}
                        </option>
                      ))}
                    </select>
                    {workPeriodId && (
                      <button
                        type="button"
                        onClick={() => void handlePull()}
                        disabled={pulling || workPeriod?.closed_at != null}
                        className="btn btn-blue btn-sm"
                        title={workPeriod?.closed_at ? 'Period is closed' : undefined}
                      >
                        {pulling ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3.5 h-3.5" />
                        )}
                        {pulling ? 'Pulling…' : 'Pull from Xero'}
                      </button>
                    )}
                  </>
                )}
              </div>

              {pullSummary && (
                <p className="text-[12px] text-ccaanz-grey mt-1.5">
                  {pullSummary.aggregateCount} donor–currency pair
                  {pullSummary.aggregateCount !== 1 ? 's' : ''} ({pullSummary.uniqueDonorCount}{' '}
                  unique donor{pullSummary.uniqueDonorCount !== 1 ? 's' : ''}) · last pulled{' '}
                  {formatDate(pullSummary.pulledAt)}
                </p>
              )}
              {pullError && <p className="text-[12px] text-red-600 mt-1">{pullError}</p>}

              {/* Donor list */}
              {donorsLoading ? (
                <div className="flex items-center gap-2 text-[13px] text-ccaanz-grey mt-4">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : workPeriodId && donors.length > 0 ? (
                <div className="mt-4">
                  {(() => {
                    const unissuedCount = donors.filter((d) => {
                      const k = `${d.contactId}:${d.currency}`;
                      const issued = receiptsForPeriod.find(
                        (r) =>
                          r.contact_id === d.contactId &&
                          r.currency === d.currency &&
                          r.status !== 'superseded',
                      );
                      return !issued && !issueOutcomes[k] && d.email;
                    }).length;
                    const closed = workPeriod?.closed_at != null;
                    return (
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] font-medium text-ccaanz-grey uppercase tracking-wide">
                          Eligible donors
                        </p>
                        {unissuedCount > 0 && !closed && (
                          <button
                            type="button"
                            onClick={() => void handleIssueAll()}
                            disabled={issuingAll}
                            className="btn btn-blue btn-sm"
                          >
                            {issuingAll ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                {issueAllProgress
                                  ? `Issuing ${issueAllProgress.done} of ${issueAllProgress.total}…`
                                  : 'Starting…'}
                              </>
                            ) : (
                              <>
                                <Check className="w-3.5 h-3.5" />
                                Issue all ({unissuedCount})
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="border-b border-paper-divider text-left text-ccaanz-grey">
                          <th className="py-2 pr-3 font-medium">Donor</th>
                          <th className="py-2 pr-3 font-medium">Email</th>
                          <th className="py-2 pr-3 font-medium text-right">Amount</th>
                          <th className="py-2 pr-3 font-medium">Tracking</th>
                          <th className="py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-paper-divider">
                        {donors.map((d) => {
                          const key = `${d.contactId}:${d.currency}`;
                          const isEditing = editingDonorKey === key;
                          const isSaving = savingDonorKey === key;

                          if (isEditing) {
                            return (
                              <tr key={key}>
                                <td colSpan={5} className="py-3 px-1">
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[12px] font-medium text-ccaanz-black">
                                        {d.displayName}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={cancelEditDonor}
                                        disabled={isSaving}
                                        className="text-ccaanz-grey hover:text-ccaanz-black p-1 rounded"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="col-span-2">
                                        <label
                                          htmlFor="receipts-donor-email"
                                          className="text-[10px] text-ccaanz-grey uppercase tracking-wide block mb-0.5"
                                        >
                                          Email
                                        </label>
                                        <input
                                          id="receipts-donor-email"
                                          className="input text-[12px] py-1"
                                          value={editDonorValues.email}
                                          onChange={(e) =>
                                            setEditDonorValues((v) => ({
                                              ...v,
                                              email: e.target.value,
                                            }))
                                          }
                                          placeholder="donor@example.com"
                                          // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after an explicit edit-donor action
                                          autoFocus
                                        />
                                      </div>
                                      <div className="col-span-2">
                                        <label
                                          htmlFor="receipts-donor-addr-line1"
                                          className="text-[10px] text-ccaanz-grey uppercase tracking-wide block mb-0.5"
                                        >
                                          Address line 1
                                        </label>
                                        <input
                                          id="receipts-donor-addr-line1"
                                          className="input text-[12px] py-1"
                                          value={editDonorValues.addr_line1}
                                          onChange={(e) =>
                                            setEditDonorValues((v) => ({
                                              ...v,
                                              addr_line1: e.target.value,
                                            }))
                                          }
                                        />
                                      </div>
                                      <div className="col-span-2">
                                        <label
                                          htmlFor="receipts-donor-addr-line2"
                                          className="text-[10px] text-ccaanz-grey uppercase tracking-wide block mb-0.5"
                                        >
                                          Address line 2
                                        </label>
                                        <input
                                          id="receipts-donor-addr-line2"
                                          className="input text-[12px] py-1"
                                          value={editDonorValues.addr_line2}
                                          onChange={(e) =>
                                            setEditDonorValues((v) => ({
                                              ...v,
                                              addr_line2: e.target.value,
                                            }))
                                          }
                                        />
                                      </div>
                                      <div>
                                        <label
                                          htmlFor="receipts-donor-city"
                                          className="text-[10px] text-ccaanz-grey uppercase tracking-wide block mb-0.5"
                                        >
                                          City
                                        </label>
                                        <input
                                          id="receipts-donor-city"
                                          className="input text-[12px] py-1"
                                          value={editDonorValues.city}
                                          onChange={(e) =>
                                            setEditDonorValues((v) => ({
                                              ...v,
                                              city: e.target.value,
                                            }))
                                          }
                                        />
                                      </div>
                                      <div>
                                        <label
                                          htmlFor="receipts-donor-postcode"
                                          className="text-[10px] text-ccaanz-grey uppercase tracking-wide block mb-0.5"
                                        >
                                          Postcode
                                        </label>
                                        <input
                                          id="receipts-donor-postcode"
                                          className="input text-[12px] py-1"
                                          value={editDonorValues.postcode}
                                          onChange={(e) =>
                                            setEditDonorValues((v) => ({
                                              ...v,
                                              postcode: e.target.value,
                                            }))
                                          }
                                        />
                                      </div>
                                    </div>
                                    {donorEditError && (
                                      <p className="text-[11px] text-red-600">{donorEditError}</p>
                                    )}
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => void saveEditDonor(d)}
                                        disabled={isSaving}
                                        className="btn btn-blue btn-sm"
                                      >
                                        {isSaving ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <Check className="w-3.5 h-3.5" />
                                        )}
                                        {isSaving ? 'Saving…' : 'Save'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={cancelEditDonor}
                                        disabled={isSaving}
                                        className="btn btn-ghost btn-sm"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          const existingReceipt = receiptsForPeriod.find(
                            (r) =>
                              r.contact_id === d.contactId &&
                              r.currency === d.currency &&
                              r.status !== 'superseded',
                          );
                          const alreadyIssued = existingReceipt != null;
                          const issuing = issuingContact === key;
                          const previewing = previewLoading === key;
                          const outcome = issueOutcomes[key];
                          const issueError = issueErrors[key];
                          const noEmail = !d.email;
                          const closed = workPeriod?.closed_at != null;

                          return (
                            <tr key={key}>
                              <td className="py-2 pr-3 text-ccaanz-black">{d.displayName}</td>
                              <td className="py-2 pr-3">
                                {d.email ? (
                                  <span className="text-ccaanz-grey truncate max-w-[160px] block">
                                    {d.email}
                                  </span>
                                ) : (
                                  <span className="text-red-600 font-medium">⚠ No email</span>
                                )}
                              </td>
                              <td className="py-2 pr-3 text-right num text-ccaanz-black whitespace-nowrap">
                                {formatAmount(d.totalAmount, d.currency)}
                              </td>
                              <td className="py-2 pr-3 text-ccaanz-grey">
                                {d.primaryTracking ?? '—'}
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => startEditDonor(d)}
                                    disabled={issuingAll}
                                    className="btn btn-ghost btn-sm"
                                    title="Edit email / address"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handlePreview(d.contactId, d.currency)}
                                    disabled={previewing}
                                    className="btn btn-ghost btn-sm whitespace-nowrap"
                                  >
                                    {previewing ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <FileText className="w-3.5 h-3.5" />
                                    )}
                                    Preview
                                  </button>

                                  {alreadyIssued || outcome ? (
                                    <span className="text-[12px] text-green-700 flex items-center gap-1 whitespace-nowrap">
                                      <Check className="w-3 h-3" />
                                      {outcome?.receiptNumber ?? existingReceipt?.receipt_number}
                                      <a
                                        href="https://mail.google.com/mail/#drafts"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline ml-1"
                                      >
                                        Drafts ↗
                                      </a>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => void handleIssue(d.contactId, d.currency)}
                                      disabled={issuing || noEmail || closed || issuingAll}
                                      className="btn btn-blue btn-sm whitespace-nowrap"
                                      title={
                                        noEmail
                                          ? 'Email address required — click pencil to edit'
                                          : closed
                                            ? 'Period is closed'
                                            : undefined
                                      }
                                    >
                                      {issuing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                      {issuing ? 'Issuing…' : 'Issue & Draft'}
                                    </button>
                                  )}
                                </div>
                                {issueError && (
                                  <p className="text-[11px] text-red-600 mt-0.5">{issueError}</p>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : workPeriodId && !donorsLoading ? (
                <p className="text-[13px] text-ccaanz-grey italic mt-4">
                  No donors pulled yet — click "Pull from Xero" to load eligible donors.
                </p>
              ) : null}

              {/* Issued receipts */}
              {receiptsForPeriod.length > 0 && (
                <div className="mt-6 border-t border-paper-divider pt-4">
                  <p className="text-[11px] font-medium text-ccaanz-grey uppercase tracking-wide mb-2">
                    Issued receipts ({receiptsForPeriod.length})
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="border-b border-paper-divider text-left text-ccaanz-grey">
                          <th className="py-2 pr-3 font-medium">Receipt #</th>
                          <th className="py-2 pr-3 font-medium">Donor</th>
                          <th className="py-2 pr-3 font-medium text-right">Amount</th>
                          <th className="py-2 pr-3 font-medium">Status</th>
                          <th className="py-2 pr-3 font-medium">Issued</th>
                          <th className="py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-paper-divider">
                        {receiptsForPeriod.map((r) => {
                          const isReissuing = reissuingId === r.id;
                          const reissueError = reissueErrors[r.id];
                          const closed = workPeriod?.closed_at != null;
                          return (
                            <tr
                              key={r.id}
                              className={r.status === 'superseded' ? 'opacity-50' : ''}
                            >
                              <td className="py-2 pr-3 num text-ccaanz-black">
                                {r.receipt_number}
                              </td>
                              <td className="py-2 pr-3 text-ccaanz-black">{r.display_name}</td>
                              <td className="py-2 pr-3 text-right num whitespace-nowrap">
                                {formatAmount(r.total_amount, r.currency)}
                              </td>
                              <td className="py-2 pr-3">
                                <StatusBadge status={r.status} />
                              </td>
                              <td className="py-2 pr-3 text-ccaanz-grey whitespace-nowrap">
                                {formatDate(r.created_at)}
                              </td>
                              <td className="py-2">
                                {/* 'failed' = orphaned re-issue tombstone (cca-zah8):
                                    no PDF and the original is still live, so no
                                    actions — the registrar re-issues the original. */}
                                {r.status !== 'superseded' && r.status !== 'failed' && (
                                  <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {!r.xero_attachment_id && (
                                        <button
                                          type="button"
                                          onClick={() => void handleXeroAttach(r.id)}
                                          disabled={attachingId === r.id}
                                          className="btn btn-ghost btn-sm whitespace-nowrap"
                                          title="Attach the PDF to the donor's Xero contact record"
                                        >
                                          {attachingId === r.id ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <Paperclip className="w-3.5 h-3.5" />
                                          )}
                                          {attachingId === r.id ? 'Attaching…' : 'Attach to Xero'}
                                        </button>
                                      )}
                                      {r.xero_attachment_id && (
                                        <span className="text-[12px] text-green-700 flex items-center gap-1 whitespace-nowrap">
                                          <Check className="w-3 h-3" />
                                          In Xero
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => void handleReissue(r.id)}
                                        disabled={isReissuing || closed}
                                        className="btn btn-ghost btn-sm whitespace-nowrap text-ccaanz-grey"
                                        title={
                                          closed
                                            ? 'Period is closed'
                                            : 'Re-pull from Xero and create a new corrected draft'
                                        }
                                      >
                                        {isReissuing ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <RefreshCw className="w-3.5 h-3.5" />
                                        )}
                                        {isReissuing ? 'Re-issuing…' : 'Re-issue'}
                                      </button>
                                    </div>
                                    {reissueError && (
                                      <p className="text-[11px] text-red-600 mt-0.5">
                                        {reissueError}
                                      </p>
                                    )}
                                    {attachErrors[r.id] && (
                                      <p className="text-[11px] text-red-600 mt-0.5">
                                        {attachErrors[r.id]}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Giving Periods ──────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-section">
          <SectionHeader
            title="Giving periods"
            sectionId="periods"
            collapsed={!!collapsed.periods}
            onToggle={toggleSection}
            action={
              !showNewPeriod ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowNewPeriod(true);
                    setPeriodError(null);
                  }}
                  className="btn btn-blue btn-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New period
                </button>
              ) : undefined
            }
          />
          {!collapsed.periods && (
            <>
              <p className="panel-section-sub">
                Each period defines a giving window for receipt issuance. The NZ donation tax credit
                year runs 1 Apr – 31 Mar.
              </p>

              {loading ? (
                <div className="flex items-center gap-2 text-[13px] text-ccaanz-grey">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <>
                  {periods.length === 0 && !showNewPeriod && (
                    <p className="text-[13px] text-ccaanz-grey italic">
                      No periods yet — create one to start issuing receipts.
                    </p>
                  )}

                  <div className="space-y-3">
                    {periods.map((p) => (
                      <div
                        key={p.id}
                        className="border border-paper-divider rounded-control px-4 py-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-[14px] font-medium text-ccaanz-black">
                                {p.label}
                              </span>
                              <span className="text-[11px] text-ccaanz-grey num">({p.id})</span>
                            </div>
                            <div className="text-[12px] text-ccaanz-grey space-y-0.5">
                              <div>
                                {formatDate(p.start_date)} – {formatDate(p.end_date)}
                                <span className="ml-3">Issue date: {formatDate(p.issue_date)}</span>
                              </div>
                              {editingNextNum === p.id ? (
                                <div className="mt-1">
                                  <div className="flex items-center gap-2">
                                    <span className="num text-ccaanz-black">
                                      {p.number_prefix}-
                                    </span>
                                    <input
                                      type="number"
                                      className="input w-24 py-0.5 px-2 text-[12px]"
                                      value={editNextVal}
                                      min={p.next_number + 1}
                                      onChange={(e) => setEditNextVal(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') void saveNextNum(p);
                                        if (e.key === 'Escape') cancelEditNextNum();
                                      }}
                                      // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after an explicit edit action
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      onClick={() => void saveNextNum(p)}
                                      disabled={savingNextNum === p.id}
                                      className="btn btn-blue btn-sm"
                                    >
                                      {savingNextNum === p.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Check className="w-3 h-3" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelEditNextNum}
                                      disabled={savingNextNum === p.id}
                                      className="btn btn-ghost btn-sm"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  {nextNumError && (
                                    <p className="text-[11px] text-red-600 mt-1">{nextNumError}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <span className="num">
                                    Next receipt: {p.number_prefix}-{p.next_number}
                                  </span>
                                  {!p.closed_at && (
                                    <button
                                      type="button"
                                      onClick={() => startEditNextNum(p)}
                                      className="text-ccaanz-grey hover:text-ccaanz-black p-0.5 rounded"
                                      title="Set next receipt number"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`pill ${p.closed_at ? 'pill-warn' : 'pill-success'}`}>
                              {p.closed_at ? 'closed' : 'open'}
                            </span>
                            {!p.closed_at && (
                              <button
                                type="button"
                                onClick={() => void handleClosePeriod(p.id)}
                                disabled={closingPeriod === p.id}
                                className="btn btn-ghost btn-sm text-amber-600 hover:text-amber-800"
                                title="Close this period — prevents further issuance"
                              >
                                {closingPeriod === p.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Lock className="w-3.5 h-3.5" />
                                )}
                                Close period
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {showNewPeriod && (
                    <form
                      onSubmit={(e) => void handleCreatePeriod(e)}
                      className="mt-4 border border-paper-divider rounded-control px-4 py-4 space-y-3 bg-white"
                    >
                      <h3 className="text-[13px] font-medium text-ccaanz-black">
                        New giving period
                      </h3>

                      {periodError && <p className="text-[12px] text-red-600">{periodError}</p>}

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label
                            htmlFor="receipts-period-id"
                            className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                          >
                            Period ID *
                          </label>
                          <input
                            id="receipts-period-id"
                            className="input"
                            placeholder="e.g. fy2526"
                            value={newPeriod.id}
                            onChange={(e) => handlePeriodIdChange(e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="receipts-period-label"
                            className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                          >
                            Label *
                          </label>
                          <input
                            id="receipts-period-label"
                            className="input"
                            placeholder="e.g. FY 2025–26"
                            value={newPeriod.label}
                            onChange={(e) => setNewPeriod((p) => ({ ...p, label: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="receipts-period-start-date"
                            className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                          >
                            Start date *
                          </label>
                          <input
                            id="receipts-period-start-date"
                            className="input"
                            type="date"
                            value={newPeriod.start_date}
                            onChange={(e) =>
                              setNewPeriod((p) => ({ ...p, start_date: e.target.value }))
                            }
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="receipts-period-end-date"
                            className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                          >
                            End date *
                          </label>
                          <input
                            id="receipts-period-end-date"
                            className="input"
                            type="date"
                            value={newPeriod.end_date}
                            onChange={(e) =>
                              setNewPeriod((p) => ({ ...p, end_date: e.target.value }))
                            }
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="receipts-period-issue-date"
                            className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                          >
                            Issue date *
                          </label>
                          <input
                            id="receipts-period-issue-date"
                            className="input"
                            type="date"
                            value={newPeriod.issue_date}
                            onChange={(e) =>
                              setNewPeriod((p) => ({ ...p, issue_date: e.target.value }))
                            }
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="receipts-period-prefix"
                            className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                          >
                            Receipt prefix
                          </label>
                          <input
                            id="receipts-period-prefix"
                            className="input"
                            placeholder={derivePrefix(newPeriod.id) || 'e.g. 202526'}
                            value={newPeriod.number_prefix}
                            onChange={(e) =>
                              setNewPeriod((p) => ({ ...p, number_prefix: e.target.value }))
                            }
                          />
                        </div>
                      </div>

                      <p className="text-[11px] text-ccaanz-grey">
                        Receipt prefix auto-derived from ID if left blank (fy2526 → 202526).
                      </p>

                      <div className="flex gap-2 pt-1">
                        <button
                          type="submit"
                          disabled={creatingPeriod}
                          className="btn btn-blue btn-sm"
                        >
                          {creatingPeriod ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Plus className="w-3.5 h-3.5" />
                          )}
                          {creatingPeriod ? 'Creating…' : 'Create period'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewPeriod(false);
                            setNewPeriod({ ...BLANK_PERIOD });
                            setPeriodError(null);
                          }}
                          disabled={creatingPeriod}
                          className="btn btn-ghost btn-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Organisation ────────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-section">
          <SectionHeader
            title="Organisation"
            sectionId="org"
            collapsed={!!collapsed.org}
            onToggle={toggleSection}
          />
          {!collapsed.org && (
            <>
              <p className="panel-section-sub">
                These values appear on every receipt PDF. Set IRD number and Charities registration
                before issuing receipts.
              </p>
              {loading ? (
                <div className="flex items-center gap-2 text-[13px] text-ccaanz-grey py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="divide-y divide-paper-divider">
                  {ORG_CONFIG_ORDER.map((key) =>
                    renderConfigField(key, ORG_CONFIG[key] ?? '', false),
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Email template ──────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-section">
          <SectionHeader
            title="Email template"
            sectionId="email"
            collapsed={!!collapsed.email}
            onToggle={toggleSection}
          />
          {!collapsed.email && (
            <>
              <p className="panel-section-sub">
                Customise the draft email sent with each receipt. Body supports template variables:{' '}
                <span className="font-mono text-[11px]">
                  {'{firstName}'} {'{total}'} {'{receiptNumber}'} {'{periodStart}'} {'{periodEnd}'}{' '}
                  {'{givingReason}'}
                </span>
                . Signature field accepts HTML — use{' '}
                <span className="font-mono text-[11px]">{'<img src="…" width="120">'}</span> for a
                logo at a public URL.
              </p>
              {loading ? (
                <div className="flex items-center gap-2 text-[13px] text-ccaanz-grey py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="divide-y divide-paper-divider">
                  {EMAIL_CONFIG_ORDER.map((key) => {
                    const field = EMAIL_CONFIG[key];
                    if (!field) return null;
                    return renderConfigField(
                      key,
                      field.label,
                      field.multiline,
                      field.placeholder,
                      field.hideValue,
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Giving reasons ──────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-section">
          <SectionHeader
            title="Giving reasons"
            sectionId="reasons"
            collapsed={!!collapsed.reasons}
            onToggle={toggleSection}
            action={
              !showNewMapping ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowNewMapping(true);
                    setMappingError(null);
                  }}
                  className="btn btn-blue btn-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add mapping
                </button>
              ) : undefined
            }
          />
          {!collapsed.reasons && (
            <>
              <p className="panel-section-sub">
                Maps a Xero Tracking category to the paragraph that appears on the receipt
                explaining why the donation was given. The donor's primary tracking category
                determines which paragraph is used.
              </p>

              {loading ? (
                <div className="flex items-center gap-2 text-[13px] text-ccaanz-grey">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <>
                  {mappings.length === 0 && !showNewMapping && (
                    <p className="text-[13px] text-ccaanz-grey italic">
                      No mappings yet. Add one per Xero Tracking category used in donations.
                    </p>
                  )}

                  <div className="space-y-2">
                    {mappings.map((m) => (
                      <div
                        key={m.category}
                        className="border border-paper-divider rounded-control px-4 py-3"
                      >
                        {editingMapping === m.category ? (
                          <div className="space-y-2">
                            <div className="text-[13px] font-medium text-ccaanz-black">
                              {m.category}
                            </div>
                            <textarea
                              className="input resize-y min-h-[80px]"
                              value={editMappingReason}
                              onChange={(e) => setEditMappingReason(e.target.value)}
                              // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after an explicit Edit action
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => void saveEditMapping(m.category)}
                                disabled={savingMapping === m.category}
                                className="btn btn-blue btn-sm"
                              >
                                {savingMapping === m.category ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Check className="w-3.5 h-3.5" />
                                )}
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditMapping}
                                disabled={savingMapping === m.category}
                                className="btn btn-ghost btn-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-[13px] font-medium text-ccaanz-black mb-1">
                                {m.category}
                              </div>
                              <div className="text-[12px] text-ccaanz-grey leading-relaxed whitespace-pre-wrap">
                                {m.reason}
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex gap-1">
                              <button
                                type="button"
                                onClick={() => startEditMapping(m)}
                                className="text-ccaanz-grey hover:text-ccaanz-black p-1 rounded"
                                title="Edit"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => void deleteMapping(m.category)}
                                disabled={deletingMapping === m.category}
                                className="text-ccaanz-grey hover:text-ccaanz-red p-1 rounded"
                                title="Delete"
                              >
                                {deletingMapping === m.category ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {showNewMapping && (
                    <form
                      onSubmit={(e) => void handleCreateMapping(e)}
                      className="mt-3 border border-paper-divider rounded-control px-4 py-4 space-y-3 bg-white"
                    >
                      <h3 className="text-[13px] font-medium text-ccaanz-black">New mapping</h3>
                      {mappingError && <p className="text-[12px] text-red-600">{mappingError}</p>}
                      <div>
                        <label
                          htmlFor="receipts-mapping-category"
                          className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                        >
                          Xero Tracking category *
                        </label>
                        {!xeroConnected ? (
                          <p className="text-[12px] text-amber-600">
                            Xero is not connected — connect Xero first to populate this list.
                          </p>
                        ) : trackingOptions.length === 0 ? (
                          <p className="text-[12px] text-ccaanz-grey italic">
                            No active options found in the "Tracking" category in Xero.
                          </p>
                        ) : (
                          <select
                            id="receipts-mapping-category"
                            className="input"
                            value={newMappingCategory}
                            onChange={(e) => setNewMappingCategory(e.target.value)}
                            required
                          >
                            <option value="">— select a category —</option>
                            {trackingOptions
                              .filter((opt) => !mappings.some((m) => m.category === opt))
                              .map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="receipts-mapping-reason"
                          className="text-[11px] text-ccaanz-grey uppercase tracking-wide block mb-1"
                        >
                          Giving reason paragraph *
                        </label>
                        <textarea
                          id="receipts-mapping-reason"
                          className="input resize-y min-h-[100px]"
                          placeholder="e.g. Your donations supported the general ministry of Confessing Anglicans…"
                          value={newMappingReason}
                          onChange={(e) => setNewMappingReason(e.target.value)}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={creatingMapping}
                          className="btn btn-blue btn-sm"
                        >
                          {creatingMapping ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Plus className="w-3.5 h-3.5" />
                          )}
                          {creatingMapping ? 'Saving…' : 'Save mapping'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewMapping(false);
                            setNewMappingCategory('');
                            setNewMappingReason('');
                            setMappingError(null);
                          }}
                          disabled={creatingMapping}
                          className="btn btn-ghost btn-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
